import "./app.css";
import App from "./App.svelte";
import { mount } from "svelte";
import { currentTab, showcase, isShowcaseRequest } from "./lib/nav";
import { hasNativeShell } from "./bridge/client";
import { debugSearch } from "./lib/debugSearch";
import { removeBootSkeleton } from "./lib/bootSkeleton";
import { installDumpHelper } from "./lib/club/visibleSets";

// Build 16 parity proof (docs/build-16.md): read-only, reports the sets on screen.
installDumpHelper(window);

const target = document.getElementById("app");
if (!target) throw new Error("Missing #app mount target");

/**
 * Showcase boot path — the PUBLIC swimset.club page.
 *
 * Which requests get it is `isShowcaseRequest` (lib/nav.ts): the share links,
 * and every path of the production web build in a plain browser. The SPA
 * fallback (`not_found_handling = 'single-page-application'` in wrangler.toml)
 * serves `index.html` for any path, and this file reads the live URL to switch
 * modes — no separate HTML entry needed.
 *
 * In showcase mode we load the published, privacy-redacted swim, adapt it via
 * the adapter (see showcaseAdapter), assign it to the EXISTING
 * `window.__SWIMSET_FIXTURE__` dev-seam the bridge already reads FIRST, flip the
 * `showcase` flag (store + body class), and force the Swim tab. App.svelte then
 * renders the REUSED swim components wrapped in the public chrome. The real
 * pipeline runs against the published swim with no new component code.
 *
 * The app boot (the native bridge present, or a dev server off the root) is the
 * fall-through: mount App, done. Everything showcase-specific is gated on the
 * decision below, so the app never touches the adapter, never fetches the
 * published JSON, and never sets the flag.
 */
const isShowcase = isShowcaseRequest(window.location, hasNativeShell(), import.meta.env.DEV);

async function boot(): Promise<void> {
  // Dev-only preview of the Club "Sets today" section (SetCompareSection):
  // `?firehoseStub=1&setComparePreview=1` with two firehose dumps injected on
  // `window.__SWIMSET_COMPARE_FIXTURES__` by the screenshot script. Dev
  // builds only (debugSearch), so a production URL can never reach it.
  const params = new URLSearchParams(debugSearch());
  if (params.get("firehoseStub") === "1" && params.get("setComparePreview") === "1") {
    const { default: SetComparePreview } = await import("./views/SetComparePreview.svelte");
    try {
      mount(SetComparePreview, { target: target! });
    } finally {
      removeBootSkeleton();
    }
    return;
  }
  if (isShowcase) {
    // Lazy-import so the adapter + published-swim fetch are out of the normal
    // boot path entirely (no cost when ?showcase=1 is absent).
    const { loadShowcaseFirehose } = await import("./lib/showcaseAdapter");
    try {
      const fh = await loadShowcaseFirehose();
      window.__SWIMSET_FIXTURE__ = fh;
    } catch (err) {
      // Surface the failure in the console; the page still mounts (the ribbon
      // section renders nothing without a fixture, and the chrome explains it).
      console.error("[showcase] failed to load published swim:", err);
    }
    showcase.set(true);
    currentTab.set("swim");
    document.body.classList.add("showcase");
  }

  try {
    mount(App, { target: target! });
  } finally {
    // Shell is mounted and interactive — drop the skeleton on the next frame so a
    // paint lands first. On a fast load the skeleton never became visible (150ms
    // fade-in), so this is silent. Try/finally ensures removal even if mount throws.
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => removeBootSkeleton());
    } else {
      removeBootSkeleton();
    }
  }
}

void boot();
